// Which attachments of a todo are shown inline under its notes (the reply capture attaches photos, stickers as
// .webp/.tgs/.webm, videos, ...). Everything else stays a row of the attachment list only.
import type { TodoAttachment } from '@/api'

export type MediaKind = 'image' | 'video' | 'tgs'

const IMAGE_TYPES = /^image\/(?:jpe?g|png|gif|webp|avif|bmp)$/i
const VIDEO_TYPES = /^video\/(?:webm|mp4|quicktime|ogg)$/i

function extension(name: string | null): string {
  const match = /\.([A-Za-z0-9]{1,5})$/.exec(name ?? '')
  return match ? match[1].toLowerCase() : ''
}

export function mediaKind(attachment: Pick<TodoAttachment, 'fmttype' | 'filename' | 'uri'>): MediaKind | null {
  const type = (attachment.fmttype ?? '').split(';')[0].trim()
  const ext = extension(attachment.filename) || extension(attachment.uri?.split(/[?#]/)[0] ?? null)
  if (type === 'application/x-tgsticker' || ext === 'tgs') return 'tgs'
  if (IMAGE_TYPES.test(type)) return 'image'
  if (VIDEO_TYPES.test(type)) return 'video'
  if (!type) {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp'].includes(ext)) return 'image'
    if (['webm', 'mp4', 'mov'].includes(ext)) return 'video'
  }
  return null
}

// Only our own signed file links are loaded by the page (an external link would leak the viewer's address to
// whoever hosts it); inline attachments (no uri) are fetched with the session through the API.
export function isOwnFileLink(uri: string | null, origin: string = window.location.origin): boolean {
  if (!uri) return false
  try {
    const url = new URL(uri, origin)
    return url.origin === origin && url.pathname.startsWith('/api/files/')
  } catch {
    return false
  }
}

export async function gunzipJson(bytes: ArrayBuffer): Promise<unknown> {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))
  return JSON.parse(await new Response(stream).text())
}
